'use client'

import { useMemo, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { useAddAdToBoardMutation, useCreateAdBoardMutation } from '@/api/ad-boards/mutations/ad-board-mutations'
import { GetAdBoardsQuery, useGetAdBoardsQuery, type IAdBoard } from '@/api/ad-boards/queries/get-ad-boards-query'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { AddToBoardButton } from '@/features/ad-boards/add-to-board-button'
import { toast } from '@/features/toast/toast'
import { LOCAL_STORAGE_KEYS, useLocalStorage } from '@/setup/local-storage'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import { cn } from '@/utils/styles/cn'
import { IClassNameProps } from '@/utils/typings/class-name-props'

type IBoardRef = { uuid: string; name: string }

/** Created on the very first save of a brand that has no one-click-safe board yet. */
const DEFAULT_BOARD_NAME = 'My ads'

/**
 * A board the automatic fallback may write to on its own: private and hand-curated. Public boards
 * would publish the ad on the brand's public page, and rule-managed boards are system-owned — both
 * stay reachable through the picker, where choosing them is explicit.
 */
const isOneClickSafe = (board: IAdBoard) => board.visibility === 'PRIVATE' && !board.hasRule

/**
 * The one-click destination: the remembered board while it still exists (an explicit pick, so any
 * visibility counts), else the most recent one-click-safe board, else null (the save creates the
 * default board). Resolving against the live list is what heals a stored board that was archived
 * or deleted since.
 */
function resolveOneClickTarget(boards: IAdBoard[], lastUsed: IBoardRef | null): IBoardRef | null {
    if (lastUsed && boards.some((board) => board.uuid === lastUsed.uuid)) return lastUsed
    const fallback = boards.find(isOneClickSafe)
    return fallback ? { uuid: fallback.uuid, name: fallback.name } : null
}

/**
 * One default-board creation per brand shared by every mounted split button — quick saves on
 * several cards of a board-less brand must resolve to one "My ads" board, not one each. A
 * failed or empty attempt is evicted so the next click can retry.
 */
const defaultBoardByBrand = new Map<string, Promise<IBoardRef | null>>()

function getOrCreateDefaultBoard(
    brandSlug: string,
    create: (body: { name: string }) => Promise<{ uuid: string; name: string } | null>
): Promise<IBoardRef | null> {
    let pending = defaultBoardByBrand.get(brandSlug)
    if (!pending) {
        pending = create({ name: DEFAULT_BOARD_NAME })
            .then((board) => {
                if (!board) {
                    defaultBoardByBrand.delete(brandSlug)
                    return null
                }
                return { uuid: board.uuid, name: board.name }
            })
            .catch(() => {
                defaultBoardByBrand.delete(brandSlug)
                return null
            })
        defaultBoardByBrand.set(brandSlug, pending)
    }
    return pending
}

type ISaveToBoardSplitButtonProps = IClassNameProps & {
    adUuid: string
    /** A board the ad is already on per the server payload — the button mounts in its saved state. */
    initialSavedBoard?: IBoardRef | null
    disabled?: boolean
}

/**
 * The card's always-visible save action (Pinterest pattern): the main segment saves the ad to a
 * default board in ONE click — last-used board first, else the brand's most recently active
 * private non-rule board, else a "My ads" board created on the spot — and its label names that
 * target ("Save on <board>") so the destination is never a surprise. The chevron opens the board
 * picker for everything else (other boards, search, creation). The button hugs its label; when the
 * footer squeezes it, the label truncates and the tooltip carries the full destination.
 */
export const SaveToBoardSplitButton = ({
    adUuid,
    initialSavedBoard = null,
    disabled = false,
    className,
}: ISaveToBoardSplitButtonProps) => {
    const { api, apiParams, qc } = useApiToolbox()
    const addToBoard = useAddAdToBoardMutation()
    const createBoard = useCreateAdBoardMutation()
    // No per-ad param, so every card on the grid shares one cached brand-scoped query.
    const { data: boards } = useGetAdBoardsQuery()

    /**
     * The same list `useGetAdBoardsQuery` serves, fetchable on demand: a save clicked before the
     * hook's fetch lands must WAIT for the list (identical key, so this joins the in-flight
     * request) rather than no-op — the first click always completes.
     */
    const fetchBoards = () => {
        const params = { ...apiParams, archived: undefined, forAdUuid: undefined }
        return qc.fetchQuery({
            queryKey: GetAdBoardsQuery.getKey(params),
            queryFn: () => GetAdBoardsQuery.call(api, params),
        })
    }

    // Registry-keyed and event-synced: every mounted card reads the same value and re-renders
    // when any card (or the picker) records a new last-used board.
    const { storedValue: lastUsedStored, setValue: setLastUsed } = useLocalStorage<IBoardRef | null>({
        key: LOCAL_STORAGE_KEYS.AdBoards.LastUsedBoard(apiParams.brandSlug),
        initialValue: null,
    })
    const lastUsed = lastUsedStored ?? null

    /**
     * Session override on top of the server-known membership: `undefined` means untouched (the
     * payload's board shows), a board means saved in this session, `null` means removed in this
     * session. Deriving `savedTo` — rather than copying the payload into state — keeps the server
     * data authoritative when the ads query refetches.
     */
    const [sessionSaved, setSessionSaved] = useState<IBoardRef | null | undefined>(undefined)
    const savedTo = sessionSaved === undefined ? initialSavedBoard : sessionSaved
    const [isSaving, setIsSaving] = useState(false)

    // Label preview while the list loads (unvalidated); `save` always resolves against the
    // fetched list, so a stale remembered board can be shown briefly but never written to.
    const target = useMemo<IBoardRef | null>(
        () => (boards ? resolveOneClickTarget(boards, lastUsed) : lastUsed),
        [boards, lastUsed]
    )

    const markSaved = (board: IBoardRef) => {
        setLastUsed(board)
        setSessionSaved(board)
    }

    const save = async () => {
        if (savedTo || isSaving) return
        setIsSaving(true)
        try {
            // The loaded list is the precondition for every write — a click during the initial
            // fetch waits for it (spinner showing) instead of no-opping or, worse, concluding
            // "this brand has no boards" and creating one prematurely.
            const list = boards ?? (await fetchBoards())
            // Creation failures surface through the create mutation's own error toast.
            const board =
                resolveOneClickTarget(list, lastUsed) ??
                (await getOrCreateDefaultBoard(apiParams.brandSlug, createBoard.mutateAsync))
            if (!board) return
            await addToBoard.mutateAsync({ boardUuid: board.uuid, adUuid })
            markSaved(board)
            toast.success(`Saved to ${board.name}`)
        } catch {
            toast.error('Could not save to board')
        } finally {
            setIsSaving(false)
        }
    }

    const label = savedTo ? `Saved on ${savedTo.name}` : target ? `Save on ${target.name}` : 'Save'

    return (
        // The stock split-button pairing (see the admin support page's add-user control): two
        // plain buttons, facing corners squared, the left one dropping its facing border. Stock
        // components mean stock type scale, radii, and disabled treatment — nothing hand-rolled.
        <div className={cn('flex min-w-0 items-center', className)}>
            <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                    <Button
                        variant="ds2-primary"
                        size="ds2-sm"
                        // The saved state stays clickable-looking but inert (the guard in `save`)
                        // rather than `disabled` — dimming only half a split button reads broken.
                        // ds2-primary's before/after gradient overlays carry their own radius, so
                        // the facing side squares them too — otherwise hover (which brightens the
                        // overlays) rounds the seam corners back in.
                        className={cn(
                            'min-w-0 rounded-r-none border-r-0 before:rounded-r-none after:rounded-r-none',
                            savedTo && 'cursor-default'
                        )}
                        onClick={save}
                        disabled={disabled}
                        isLoading={isSaving}
                        aria-pressed={Boolean(savedTo)}
                    >
                        {savedTo ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
                        <span className="min-w-0 truncate">{label}</span>
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    {savedTo
                        ? `Saved on ${savedTo.name}`
                        : target
                          ? `Save on ${target.name}`
                          : boards
                            ? `Saves on a new "${DEFAULT_BOARD_NAME}" board`
                            : 'Save to a board'}
                </TooltipContent>
            </Tooltip>
            <AddToBoardButton
                adUuid={adUuid}
                onAdded={markSaved}
                onRemoved={(board) => {
                    if (savedTo?.uuid === board.uuid) setSessionSaved(null)
                }}
                trigger={({ open }) => (
                    <Button
                        variant="ds2-primary"
                        size="ds2-sm"
                        disabled={disabled}
                        aria-label="Choose board"
                        className={cn(
                            'rounded-l-none px-1.5 before:rounded-l-none after:rounded-l-none',
                            open && 'bg-[#2A2928]'
                        )}
                    >
                        <ChevronDown className="h-4 w-4" />
                    </Button>
                )}
            />
        </div>
    )
}
